import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import rehypeSanitize from "rehype-sanitize";
import { Link } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { ErrorFactory, logError } from "@core/lib/error-handling";

interface DocsViewerProps {
  docPath: string; // Ex: "01-Guide-Demarrage-Rapide.md"
  theme?: "blue" | "violet" | "emerald" | "amber";
}

export const DocsViewer: React.FC<DocsViewerProps> = ({
  docPath,
  theme = "blue",
}) => {
  const { i18n } = useTranslation();
  const lang = (i18n.language || "fr").slice(0, 2);

  const [content, setContent] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const abortController = new AbortController();

    const loadDoc = async () => {
      // Prevent execution if component unmounted or docPath changed
      if (abortController.signal.aborted) return;

      try {
        setLoading(true);

        // Candidats par locale : on tente d'abord la version localisée
        // (docs/<lang>/file.md), puis on retombe sur le FR racine (docs/file.md).
        const baseUrl = import.meta.env.BASE_URL || "/";
        const candidates =
          lang && lang !== "fr"
            ? [`${baseUrl}docs/${lang}/${docPath}`, `${baseUrl}docs/${docPath}`]
            : [`${baseUrl}docs/${docPath}`];

        // Essayer d'abord avec fetch (pour les fichiers servis par Vite)
        let fetched = false;
        for (const fullUrl of candidates) {
          try {
            const response = await fetch(fullUrl, {
              signal: abortController.signal,
            });
            if (abortController.signal.aborted) return;

            if (response.ok) {
              const content = await response.text();
              // Vérifier si on a reçu du HTML (routing intercepté) ou du markdown
              if (
                content.includes("<!doctype html>") ||
                content.includes("<html")
              ) {
                throw new Error("Routing intercepté - besoin d'import direct");
              }
              setContent(content);
              setError(null);
              fetched = true;
              break;
            }
          } catch (fetchErr) {
            if (abortController.signal.aborted) return;
            console.log("Fetch failed for", fullUrl);
          }
        }
        if (fetched) return;

        // Fallback: essayer une importation directe (développement seulement)
        if (import.meta.env.DEV && !abortController.signal.aborted) {
          try {
            // Import dynamique depuis public/docs/ (racine du projet)
            const module = await import(
              /* @vite-ignore */ `../../../../../public/docs/${docPath}?raw`
            );
            if (abortController.signal.aborted) return;

            setContent(module.default);
            setError(null);
            return;
          } catch (importErr) {
            if (abortController.signal.aborted) return;
            console.log("Direct import failed");
          }
        }

        if (!abortController.signal.aborted) {
          throw new Error(`Impossible de charger le document: ${docPath}`);
        }
      } catch (err) {
        if (abortController.signal.aborted) return;

        const errorMsg =
          err instanceof Error ? err.message : "Erreur de chargement";
        setError(errorMsg);
        logError(err, { component: "DocsViewer", metadata: { docPath } });
      } finally {
        if (!abortController.signal.aborted) {
          setLoading(false);
        }
      }
    };

    loadDoc();

    // Cleanup function to abort ongoing requests
    return () => {
      abortController.abort();
    };
  }, [docPath, lang]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg bg-red-50 dark:bg-red-900/20 p-4 text-red-800 dark:text-red-200">
        <p className="font-semibold">Erreur de chargement</p>
        <p className="text-sm">{error}</p>
      </div>
    );
  }

  const themeColors = {
    blue: "text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300",
    violet:
      "text-violet-600 dark:text-violet-400 hover:text-violet-700 dark:hover:text-violet-300",
    emerald:
      "text-emerald-600 dark:text-emerald-400 hover:text-emerald-700 dark:hover:text-emerald-300",
    amber:
      "text-amber-600 dark:text-amber-400 hover:text-amber-700 dark:hover:text-amber-300",
  };

  const linkColor = themeColors[theme] || themeColors.blue;

  return (
    <div
      className={`docs-content prose prose-lg max-w-none dark:prose-invert prose-headings:text-white prose-p:text-gray-300 prose-li:text-gray-300`}
    >
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeRaw, rehypeSanitize]}
        components={{
          // Liens internes → React Router Links
          a: ({ node, href, children, ...props }) => {
            // Ancre dans la page (table des matières)
            if (href?.startsWith("#")) {
              return (
                <a
                  href={href}
                  className={`${linkColor} underline transition-colors`}
                  onClick={(e) => {
                    e.preventDefault();
                    const id = href.substring(1);
                    const element = document.getElementById(id);
                    if (element) {
                      element.scrollIntoView({
                        behavior: "smooth",
                        block: "start",
                      });
                    }
                  }}
                  {...props}
                >
                  {children}
                </a>
              );
            }

            // Lien vers un autre document (commence par ./)
            if (href?.startsWith("./")) {
              const docName = href.replace("./", "").replace(".md", "");
              return (
                <Link
                  to={`/docs/${docName}`}
                  className={`${linkColor} underline transition-colors`}
                  {...props}
                >
                  {children}
                </Link>
              );
            }

            // Lien externe
            return (
              <a
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                className={`${linkColor} underline transition-colors inline-flex items-center gap-1`}
                {...props}
              >
                {children}
                <span className="text-xs ml-1" aria-hidden="true">
                  🔗
                </span>
              </a>
            );
          },

          // Tableaux stylés
          table: ({ node, ...props }) => (
            <div className="overflow-x-auto my-6">
              <table
                className="min-w-full divide-y divide-gray-700 dark:divide-gray-800"
                {...props}
              />
            </div>
          ),

          // Headings avec ID pour les ancres
          h1: ({ node, children, ...props }) => {
            const id = children
              ?.toString()
              .toLowerCase()
              .replace(/[^\w\s-]/g, "")
              .replace(/\s+/g, "-")
              .replace(/^-+|-+$/g, "");
            return (
              <h1
                id={id}
                className={`text-3xl font-bold mb-6 text-white`}
                {...props}
              >
                {children}
              </h1>
            );
          },
          h2: ({ node, children, ...props }) => {
            const id = children
              ?.toString()
              .toLowerCase()
              .replace(/[^\w\s-]/g, "")
              .replace(/\s+/g, "-")
              .replace(/^-+|-+$/g, "");
            return (
              <h2
                id={id}
                className={`text-2xl font-bold mt-12 mb-4 text-white border-b border-gray-800 pb-2`}
                {...props}
              >
                {children}
              </h2>
            );
          },
          h3: ({ node, children, ...props }) => {
            const id = children
              ?.toString()
              .toLowerCase()
              .replace(/[^\w\s-]/g, "")
              .replace(/\s+/g, "-")
              .replace(/^-+|-+$/g, "");
            return (
              <h3
                id={id}
                className={`text-xl font-bold mt-8 mb-3 text-white`}
                {...props}
              >
                {children}
              </h3>
            );
          },

          // Code blocks
          code: ({
            node,
            className,
            children,
            ...props
          }: React.ComponentPropsWithoutRef<"code"> & { node?: unknown }) => {
            const inline = !className || !className.includes("language-");
            if (inline) {
              const inlineColor = {
                blue: "text-blue-400 bg-blue-900/20",
                violet: "text-violet-400 bg-violet-900/20",
                emerald: "text-emerald-400 bg-emerald-900/20",
                amber: "text-amber-400 bg-amber-900/20",
              };
              return (
                <code
                  className={`rounded px-1.5 py-0.5 text-sm font-mono ${inlineColor[theme] || inlineColor.blue}`}
                  {...props}
                >
                  {children}
                </code>
              );
            }
            return (
              <code
                className="block bg-gray-950 text-gray-200 rounded-lg p-4 overflow-x-auto text-sm font-mono my-6 border border-gray-800"
                {...props}
              >
                {children}
              </code>
            );
          },
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
};
